import { useEffect, useState, useCallback } from 'react';
import { supabase } from './supabase';
import { getStoredSource } from './analytics';

const GUEST_KEY      = 'kairos_guest';
const PROFILE_KEY    = 'kairos_profile';
const MIGRATION_KEY  = 'kairos_guest_migration'; // sessionStorage bridge

function loadProfile() {
  try { return JSON.parse(localStorage.getItem(PROFILE_KEY) || 'null'); }
  catch { return null; }
}

function saveProfile(profile) {
  localStorage.setItem(PROFILE_KEY, JSON.stringify(profile));
}

async function ensureTrialSubscription(uid) {
  if (!uid) return;

  const { data: existing } = await supabase
    .from('user_subscriptions')
    .select('status')
    .eq('user_id', uid)
    .maybeSingle();

  if (existing?.status && existing.status !== 'free') return;

  const now = new Date();
  await supabase.from('user_subscriptions').upsert({
    user_id: uid,
    plan: 'kairos',
    status: 'trial',
    current_period_start: now.toISOString(),
    trial_ends_at: new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000).toISOString(),
    current_period_end: null,
    canceled_at: null,
  }, { onConflict: 'user_id' });
}

async function ensureSignupProfile(user) {
  if (!user?.id) return;
  await supabase.from('profiles').upsert({
    id: user.id,
    email: user.email || null,
    signup_at: new Date().toISOString(),
    source: getStoredSource(),
    subscription_status: 'free',
  }, { onConflict: 'id' });
}

// ── Guest migration helpers ─────────────────────────────────────────────────

/** Collect all guest localStorage data into sessionStorage before sign-up/in. */
function snapshotGuestData() {
  if (localStorage.getItem(GUEST_KEY) !== '1') return; // only for guests
  try {
    const scholarNotes = {};
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith('kairos_scholar_notes_')) {
        scholarNotes[k.replace('kairos_scholar_notes_', '')] = localStorage.getItem(k);
      }
    }
    const snapshot = {
      profile:      loadProfile(),
      journeyPrefs: JSON.parse(localStorage.getItem('kairos_journey_prefs_v1') || 'null'),
      journeyNotes: JSON.parse(localStorage.getItem('kairos_journey_notes_v1') || 'null'),
      chooseRecent: JSON.parse(localStorage.getItem('kairos_choose_recent_v1') || 'null'),
      chooseNotes:  JSON.parse(localStorage.getItem('kairos_choose_notes_v1')  || 'null'),
      scholarNotes: Object.keys(scholarNotes).length ? scholarNotes : null,
    };
    sessionStorage.setItem(MIGRATION_KEY, JSON.stringify(snapshot));
  } catch (e) {
    console.warn('[migration] snapshot failed:', e?.message || e);
  }
}

/** After sign-in, upsert the snapshot into Supabase profiles. */
async function migrateGuestData(uid) {
  try {
    const raw = sessionStorage.getItem(MIGRATION_KEY);
    if (!raw) return;
    const snap = JSON.parse(raw);

    const row = { id: uid, onboarding_done: snap.profile?.onboardingDone ?? false };
    if (snap.profile?.preferredPath) row.preferred_path = snap.profile.preferredPath;
    if (snap.profile?.hometown)      row.hometown        = snap.profile.hometown;
    if (snap.journeyPrefs)           row.journey_prefs   = snap.journeyPrefs;
    if (snap.journeyNotes && Object.keys(snap.journeyNotes).length)
                                     row.journey_notes   = snap.journeyNotes;
    if (snap.chooseRecent?.length)   row.choose_recent   = snap.chooseRecent;
    if (snap.chooseNotes && Object.keys(snap.chooseNotes).length)
                                     row.choose_notes    = snap.chooseNotes;
    if (snap.scholarNotes)           row.scholar_notes   = snap.scholarNotes;

    await supabase.from('profiles').upsert(row, { onConflict: 'id' });
    sessionStorage.removeItem(MIGRATION_KEY);
    console.log('[migration] guest data migrated to profile');
  } catch (e) {
    console.warn('[migration] failed:', e?.message || e);
  }
}

/** After sign-in, restore JSONB data from Supabase back to localStorage (cross-device sync). */
function restoreToLocalStorage(data) {
  try {
    if (data.journey_prefs)  localStorage.setItem('kairos_journey_prefs_v1', JSON.stringify(data.journey_prefs));
    if (data.journey_notes)  localStorage.setItem('kairos_journey_notes_v1', JSON.stringify(data.journey_notes));
    if (data.choose_recent)  localStorage.setItem('kairos_choose_recent_v1', JSON.stringify(data.choose_recent));
    if (data.choose_notes)   localStorage.setItem('kairos_choose_notes_v1',  JSON.stringify(data.choose_notes));
    if (data.scholar_notes) {
      Object.entries(data.scholar_notes).forEach(([id, text]) => {
        localStorage.setItem(`kairos_scholar_notes_${id}`, text);
      });
    }
  } catch (e) {
    console.warn('[migration] restore to localStorage failed:', e?.message || e);
  }
}

// ── useAuth hook ─────────────────────────────────────────────────────────────

/**
 * useAuth — central auth/session hook.
 *
 * Returns:
 *   loading      boolean   — true while checking existing session
 *   user         object|null — Supabase user (null if guest/unauthenticated)
 *   isGuest      boolean   — true if user chose "continue as guest"
 *   profile      object|null — { preferredPath, hometown, onboardingDone, tier }
 *   signUp       fn(email, password)
 *   signIn       fn(email, password)
 *   signOut      fn()
 *   continueAsGuest fn()
 *   saveOnboarding  fn(data)
 *   authError    string|null
 */
export function useAuth() {
  const [loading,   setLoading]   = useState(true);
  const [user,      setUser]      = useState(null);
  const [isGuest,   setIsGuest]   = useState(false);
  const [profile,   setProfile]   = useState(null);
  const [authError, setAuthError] = useState(null);

  // Sync profile from Supabase profiles table for authed users
  const syncProfile = useCallback(async (uid) => {
    const local = loadProfile();
    if (!uid) return local;
    try {
      const { data, error: profileErr } = await supabase
        .from('profiles')
        .select('preferred_path, hometown, language_preference, onboarding_done, tier, journey_prefs, journey_notes, choose_recent, choose_notes, scholar_notes, is_admin')
        .eq('id', uid)
        .maybeSingle();
      if (profileErr) throw profileErr;
      if (data) {
        // Restore rich data back to localStorage so existing hooks pick it up
        restoreToLocalStorage(data);
        const merged = {
          preferredPath:      data.preferred_path      || local?.preferredPath      || null,
          hometown:           data.hometown            || local?.hometown           || null,
          languagePreference: data.language_preference || local?.languagePreference || 'en',
          onboardingDone:     data.onboarding_done     ?? local?.onboardingDone     ?? false,
          tier:               data.tier                || local?.tier               || 'free',
          is_admin:           data.is_admin            ?? false,
        };
        saveProfile(merged);
        return merged;
      }
    } catch (e) { console.warn('[useAuth] profile fetch failed:', e?.message ?? e); }
    return local;
  }, []);

  // Bootstrap: check existing session
  useEffect(() => {
    let active = true;

    // Guest flag is stored locally — check it synchronously, no Supabase call.
    if (localStorage.getItem(GUEST_KEY) === '1') {
      const p = loadProfile();
      if (active) { setIsGuest(true); setProfile(p); setLoading(false); }
      return;
    }

    // Use onAuthStateChange for initial session detection instead of getSession().
    // The client's initialize() holds the Navigator lock while restoring the
    // persisted session; calling getSession() simultaneously causes lock
    // contention and a timeout. onAuthStateChange fires with INITIAL_SESSION
    // after initialize() completes, so we get the session without competing.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!active) return;
      if (session?.user) {
        // Render immediately with the cached profile from localStorage; the network
        // sync runs in the background and updates state once it returns.
        const cached = loadProfile();
        setUser(session.user);
        setProfile(cached);
        setIsGuest(false);
        localStorage.removeItem(GUEST_KEY);
        setLoading(false);

        migrateGuestData(session.user.id).catch(() => {});
        syncProfile(session.user.id).then((p) => {
          if (active && p) setProfile(p);
        });
      } else {
        // Auto sign-out (e.g. stale/invalid refresh token) — clear cached profile
        // so stale data doesn't persist the same way an explicit signOut() would.
        if (event === 'SIGNED_OUT') {
          localStorage.removeItem(PROFILE_KEY);
          localStorage.removeItem(GUEST_KEY);
        }
        setUser(null);
        setProfile(null);
        setLoading(false);
      }
    });

    return () => { active = false; subscription.unsubscribe(); };
  }, [syncProfile]);

  const signUp = useCallback(async (email, password) => {
    setAuthError(null);
    snapshotGuestData(); // capture before Supabase clears anything
    const { data, error } = await supabase.auth.signUp({ email, password });
    if (error) {
      // Friendlier message for the duplicate-email case so the user is nudged to sign in
      const msg = String(error.message || '').toLowerCase();
      if (
        msg.includes('already registered') ||
        msg.includes('already exists') ||
        msg.includes('user already') ||
        error.status === 422
      ) {
        setAuthError('This email is already registered. Try signing in instead.');
        return { ok: false, duplicate: true };
      }
      setAuthError(error.message);
      return { ok: false };
    }
    // Supabase enumeration-protection: when "Confirm email" is on, signUp
    // returns user.identities=[] for an existing email instead of an error.
    // Detect that and surface the same friendly message.
    if (data?.user && Array.isArray(data.user.identities) && data.user.identities.length === 0) {
      setAuthError('This email is already registered. Try signing in instead.');
      return { ok: false, duplicate: true };
    }
    // If no session returned, email confirmation is required — try auto sign-in
    // in case it was just a delay, but if Supabase rejects with "not confirmed",
    // surface the confirm screen instead of a raw error.
    if (!data?.session) {
      const { error: signInErr } = await supabase.auth.signInWithPassword({ email, password });
      if (signInErr) {
        const msg = String(signInErr.message || '').toLowerCase();
        if (msg.includes('not confirmed') || msg.includes('email not confirmed')) {
          return { ok: false, needsConfirm: true };
        }
        setAuthError(signInErr.message);
        return { ok: false };
      }
    }
    const { data: authData } = await supabase.auth.getUser();
    await ensureSignupProfile(authData?.user);
    await ensureTrialSubscription(authData?.user?.id);
    return { ok: true };
  }, []);

  const signIn = useCallback(async (email, password) => {
    setAuthError(null);
    snapshotGuestData(); // capture before sign-in clears guest flag
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) { setAuthError(error.message); return false; }
    return true;
  }, []);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    localStorage.removeItem(GUEST_KEY);
    localStorage.removeItem(PROFILE_KEY);
    // Clear cached data so next login starts fresh
    try {
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && (k.startsWith('kairos_insight:') || k === 'kairos_last_activity')) {
          keysToRemove.push(k);
        }
      }
      keysToRemove.forEach(k => localStorage.removeItem(k));
    } catch {}
    setUser(null);
    setIsGuest(false);
    setProfile(null);
  }, []);

  const continueAsGuest = useCallback(() => {
    localStorage.setItem(GUEST_KEY, '1');
    const existing = loadProfile();
    setIsGuest(true);
    setProfile(existing);
  }, []);

  const exitGuest = useCallback(() => {
    localStorage.removeItem(GUEST_KEY);
    setIsGuest(false);
  }, []);

  const saveOnboarding = useCallback(async (data) => {
    const next = { ...loadProfile(), ...data, onboardingDone: true };
    saveProfile(next);
    setProfile(next);
    if (user?.id) {
      try {
        await supabase.from('profiles').upsert({
          id:                  user.id,
          preferred_path:      data.preferredPath      || null,
          hometown:            data.hometown           || null,
          language_preference: data.languagePreference || 'en',
          onboarding_done:     true,
        }, { onConflict: 'id' });
      } catch (_) { /* graceful fallback */ }
    }
  }, [user]);

  const saveLanguage = useCallback(async (lang) => {
    const next = { ...loadProfile(), languagePreference: lang };
    saveProfile(next);
    setProfile(next);
    if (user?.id) {
      try {
        await supabase.from('profiles').upsert({
          id: user.id,
          language_preference: lang,
        }, { onConflict: 'id' });
      } catch (_) { /* graceful fallback */ }
    }
  }, [user]);

  return {
    loading, user, isGuest, profile,
    signUp, signIn, signOut,
    continueAsGuest, exitGuest, saveOnboarding, saveLanguage, authError,
    isAuthenticated: !!user,
    onboardingDone: profile?.onboardingDone ?? false,
  };
}

